import React, {useCallback, useEffect, useState} from 'react';
import {Linking, Platform} from 'react-native';
import {Modal as ModalPaper} from 'react-native-paper';
import {RouteProp, useNavigation, useRoute} from '@react-navigation/native';
import {useDispatch, useSelector} from 'react-redux';
import BaseScreen from 'src/components/screens/BaseScreen';
import {getPreviousRouteName} from 'src/Util';
import Settings from 'src/Settings';
import Localized from 'src/constants/AppStrings';
import AccountStore from 'src/stores/AccountStore';
import {
  fetchAccountBalanceCreditCards,
  fetchAccountBalances,
} from 'src/redux/slices/accountSlice';
import type {AppDispatch, RootState} from 'src/redux/store';
import {useNetworkState} from '../../../../hooks/useNetworkState';
import {useCreditCardSync} from '../../../../hooks/useCreditCardSync';
import {useStripeCardSetup} from '../../../../hooks/useStripeCardSetup';
import {cardScreenNavigators} from './helpers/CreditCardsNavigatorHelper';
import {NoConnectivityView} from './components/NoConnectivityView';
import {WalletContent} from './components/WalletContent';
import {AddCardModal} from './components/AddCardModal';
import {StackNavigationProp} from '@react-navigation/stack';

export interface CardsScreenProps {
  hideBackArrow?: boolean;
}
type CardsScreenNavParams = {
  CardsScreen: {hideBackArrow?: boolean} | undefined;
};

type CardsScreenNavigationProp = StackNavigationProp<
  CardsScreenNavParams,
  'CardsScreen'
>;

type CardsScreenRouteProp = RouteProp<CardsScreenNavParams, 'CardsScreen'>;

const CardsScreen: React.FC<CardsScreenProps> = ({hideBackArrow = true}) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigation = useNavigation<CardsScreenNavigationProp>();
  const route = useRoute<CardsScreenRouteProp>();

  const {hideBackArrow: routeHideBackArrow} = route.params ?? {};
  const isWalletScreenWithBackArrow = routeHideBackArrow ?? hideBackArrow;

  const {
    payrollId,
    accountId,
    displayBalances,
    defaultBalanceId,
    paymentCredentials,
    creditCards,
  } = useSelector((state: RootState) => ({
    payrollId: state.account.account?.payrollIdentifier?.value,
    accountId: state.account.account.id,
    displayBalances: state.account.account.displayBalances,
    defaultBalanceId: state.account.account.defaultBalance?.id,
    paymentCredentials: state.account.paymentCredentials,
    creditCards: state.account.creditCards,
  }));

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [previousRoute, setPreviousRoute] = useState('');

  const {isConnected, checkNetworkState} = useNetworkState();
  const {setupCreditCardListeners} = useCreditCardSync({
    accountId,
    defaultBalanceId,
    creditCards,
  });
  const {loading, addCardWithStripe} = useStripeCardSetup({
    paymentCredentials,
    onComplete: () => setIsModalVisible(false),
  });

  const navigators = cardScreenNavigators(checkNetworkState);

  useEffect(() => {
    const prevRoute = getPreviousRouteName(navigation.getState()?.routes);
    setPreviousRoute(prevRoute);
    checkNetworkState();

    const cleanup = setupCreditCardListeners();
    dispatch(fetchAccountBalances(accountId));

    return cleanup;
  }, []);

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    await checkNetworkState();
    await Promise.all([
      dispatch(fetchAccountBalances(accountId)),
      dispatch(
        fetchAccountBalanceCreditCards({
          accountId,
          accountBalanceId: defaultBalanceId,
        }),
      ),
    ]);
    setIsRefreshing(false);
  }, [accountId, defaultBalanceId, checkNetworkState, dispatch]);

  const handleAddCard = useCallback(async () => {
    const hasNetwork = await checkNetworkState();
    if (!hasNetwork) return;

    if (!AccountStore.getLocationName()) {
      navigators.showNoHomeLocationError();
      return;
    }

    const isStripeProcessor =
      paymentCredentials?.type === Settings.processorsAsString.stripe;

    if (isStripeProcessor) {
      if (Platform.OS === 'web') {
        navigators.navigateToCreditCardStripe();
      } else {
        setIsModalVisible(true);
      }
    } else {
      navigators.navigateToCreditCard();
    }
  }, [paymentCredentials, checkNetworkState, navigators]);

  const handleModalClose = useCallback(() => {
    setIsModalVisible(false);
  }, []);

  const handleAddCardComplete = useCallback(async () => {
    const hasNetwork = await checkNetworkState();
    if (!hasNetwork) {
      setIsModalVisible(false);
      return;
    }
    await addCardWithStripe();
  }, [addCardWithStripe, checkNetworkState]);

  const screenTitle = Settings.isNewUI()
    ? Localized.Labels.wallet
    : Localized.Labels.payment;

  const accessibilityLabel = Settings.isNewUI()
    ? Localized.Labels.payment
    : 'Back arrow';

  const accessibilityHint = Settings.isNewUI()
    ? Localized.Labels.payment
    : `Press to navigate back to the ${previousRoute} screen`;

  return (
    <>
      <BaseScreen
        title={screenTitle}
        testId={'qa_Wallet.PageTitle_txt'}
        previousRoute={previousRoute}
        accessibilityLabel={accessibilityLabel}
        accessibilityHint={accessibilityHint}
        hideBack={isWalletScreenWithBackArrow}
      >
        {!isConnected ? (
          <NoConnectivityView onSettingsPress={() => Linking.openSettings()} />
        ) : (
          <WalletContent
            creditCards={creditCards}
            payrollId={payrollId}
            displayBalances={displayBalances}
            isRefreshing={isRefreshing}
            onRefresh={handleRefresh}
            onCreditCardClick={navigators.navigateToCreditCardDetail}
            onPayrollClick={navigators.navigateToPayrollDeduct}
            onAddCardClick={handleAddCard}
            onAddGiftCardClick={navigators.navigateToAddGiftCard}
          />
        )}
      </BaseScreen>

      <ModalPaper
        visible={isModalVisible}
        onDismiss={handleModalClose}
        style={Platform.select({android: {justifyContent: 'flex-end'}})}
      >
        <AddCardModal
          loading={loading}
          onClose={handleModalClose}
          onAddCard={handleAddCardComplete}
        />
      </ModalPaper>
    </>
  );
};

export default CardsScreen;
